// src/Button/Button.style.ts
import {
  css,
  keyframes
} from "@emotion/react";
import { bodyB, captionSb, subTitle3 } from "@teamsparta/stack-font";
import { vars } from "@teamsparta/stack-tokens";
import { getVar } from "@teamsparta/stack-utils";
var buttonSizeVariants = {
  xs: {
    minWidth: "52px",
    height: "36px",
    paddingInline: "12px",
    gap: "2px"
  },
  sm: {
    minWidth: "56px",
    height: "40px",
    paddingInline: "12px"
  },
  md: {
    minWidth: "70px",
    height: "48px",
    paddingInline: "14px"
  },
  lg: {
    minWidth: "100px",
    height: "52px",
    gap: "2px",
    paddingInline: "16px"
  },
  xl: {
    minWidth: "154px",
    height: "56px",
    gap: "4px",
    paddingInline: "16px"
  }
};
var buttonTextVariants = {
  xs: { padding: "0 2px" },
  sm: { padding: "0 4px" },
  md: { padding: "0 4px" },
  lg: { padding: "0 4px" },
  xl: { padding: "0 4px" }
};
var buttonTextFontVariants = {
  xs: captionSb,
  sm: captionSb,
  md: bodyB,
  lg: subTitle3,
  xl: subTitle3
};
var buttonRadiusVariants = {
  xs: { borderRadius: "6px" },
  sm: { borderRadius: "6px" },
  md: { borderRadius: "8px" },
  lg: { borderRadius: "8px" },
  xl: { borderRadius: "8px" }
};
var buttonIconVariants = {
  xs: {
    svg: {
      width: "16px",
      height: "16px"
    }
  },
  sm: {
    svg: {
      width: "16px",
      height: "16px"
    }
  },
  md: {
    svg: {
      width: "16px",
      height: "16px"
    }
  },
  lg: {
    svg: {
      width: "20px",
      height: "20px"
    }
  },
  xl: {
    svg: {
      width: "20px",
      height: "20px"
    }
  }
};
var buttonLoadingVariants = {
  xs: {
    svg: {
      width: "16px",
      height: "16px"
    }
  },
  sm: {
    svg: {
      width: "16px",
      height: "16px"
    }
  },
  md: {
    svg: {
      width: "20px",
      height: "20px"
    }
  },
  lg: {
    svg: {
      width: "24px",
      height: "24px"
    }
  },
  xl: {
    svg: {
      width: "24px",
      height: "24px"
    }
  }
};
var buttonVariants = {
  // todo
  "solid-primary": {
    "--stack-button-bg-color": vars.button.solidPrimary.background,
    "--stack-button-hover-bg-color": vars.button.solidPrimary.backgroundHover,
    "--stack-button-text-color": vars.button.solidPrimary.text
  },
  "solid-secondary": {
    "--stack-button-bg-color": vars.button.solidSecondary.background,
    "--stack-button-hover-bg-color": vars.button.solidSecondary.backgroundHover,
    "--stack-button-text-color": vars.button.solidSecondary.text
  },
  "solid-tertiary": {
    "--stack-button-bg-color": vars.button.solidTertiary.background,
    "--stack-button-hover-bg-color": vars.button.solidTertiary.backgroundHover,
    "--stack-button-text-color": vars.button.solidTertiary.text
  },
  "solid-error": {
    "--stack-button-bg-color": vars.button.solidError.background,
    "--stack-button-hover-bg-color": vars.button.solidError.backgroundHover,
    "--stack-button-text-color": vars.button.solidError.text
  },
  // done
  "outline-primary": {
    "--stack-button-bg-color": vars.button.outlinePrimary.background,
    "--stack-button-hover-bg-color": vars.button.outlinePrimary.backgroundHover,
    "--stack-button-text-color": vars.button.outlinePrimary.text,
    "--stack-button-border-color": vars.button.outlinePrimary.border
  },
  "outline-secondary": {
    "--stack-button-bg-color": vars.button.outlineSecondary.background,
    "--stack-button-hover-bg-color": vars.button.outlineSecondary.backgroundHover,
    "--stack-button-text-color": vars.button.outlineSecondary.text,
    "--stack-button-border-color": vars.button.outlineSecondary.border
  },
  "outline-tertiary": {},
  "outline-error": {}
};
var buttonBaseCss = css({
  position: "relative",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  outline: "none",
  transition: "background-color 0.08s ease-in-out",
  backgroundColor: getVar("--stack-button-bg-color"),
  border: `1px solid ${getVar("--stack-button-border-color")}`,
  color: getVar("--stack-button-text-color"),
  "&:hover:not(:disabled)": {
    backgroundColor: getVar("--stack-button-hover-bg-color")
    // hover 시 배경색 변경
  },
  "&:focus": {
    outline: "revert"
  }
});
var buttonIconSizeCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media screen and (min-width: ${breakpoint}px) {
        ${buttonIconVariants[size]}
      }
    `
);
var buttonLoadingSizeCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media screen and (min-width: ${breakpoint}px) {
        ${buttonLoadingVariants[size]}
      }
    `
);
var buttonSizeCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media screen and (min-width: ${breakpoint}px) {
        ${buttonSizeVariants[size]}
      }
    `
);
var buttonTextCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media screen and (min-width: ${breakpoint}px) {
        ${buttonTextVariants[size]}
        ${buttonTextFontVariants[size]}
      }
      &&& {
        color: inherit;
      }
    `
);
var buttonRadiusCss = (sizes) => sizes.map(
  ({ breakpoint, size }) => css`
      @media screen and (min-width: ${breakpoint}px) {
        ${buttonRadiusVariants[size]}
      }
    `
);
var buttonChildrenCss = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  whiteSpace: "nowrap",
  "&[data-loading='true']": {
    opacity: 0
  }
});
var buttonRoundedCss = css({
  borderRadius: "999px"
});
var addonCss = css({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "& > svg path": {
    stroke: `${getVar("--stack-button-text-color")} !important`
  },
  "&[data-loading='true']": {
    opacity: 0
  }
});
var loadingContainerCss = css({
  cursor: "not-allowed"
});
var spin = keyframes({
  "0%": { transform: "rotate(-360deg)" },
  "100%": { transform: "rotate(0deg)" }
});
var spinnerContainerCss = css({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center"
});
var loadingCss = css({
  animation: `${spin} 1s linear infinite`,
  "& > svg stop": {
    stopColor: `${getVar("--stack-button-text-color")}`
  }
});
var fullWidthCss = css({
  width: "100%"
});
var adjustTouchableCss = css({
  display: "block",
  position: "absolute",
  minWidth: "48px",
  minHeight: "48px",
  width: "100%",
  height: "100%"
});
var disabledCss = css({
  opacity: "0.35",
  cursor: "not-allowed"
});

export {
  buttonVariants,
  buttonBaseCss,
  buttonIconSizeCss,
  buttonLoadingSizeCss,
  buttonSizeCss,
  buttonTextCss,
  buttonRadiusCss,
  buttonChildrenCss,
  buttonRoundedCss,
  addonCss,
  loadingContainerCss,
  spinnerContainerCss,
  loadingCss,
  fullWidthCss,
  adjustTouchableCss,
  disabledCss
};
